/**
 * Shared Arabic vocabulary for GUEST ARRIVAL EVIDENCE.
 *
 * Two surfaces render the one resolver (`be/services/guestArrival/resolver.js`):
 *   - BookingDetails.js        — the «حالة تسجيل دخول الضيف» card
 *   - inbox ThreadContextPanel — Guest Comms Inc 7, one line beside the thread
 *
 * D95 says the two must never disagree, and the backend half of that is already
 * structural: one resolver, one answer. THIS FILE IS THE OTHER HALF. Agreeing on
 * the data while retyping the words is the worse failure of the two — it is
 * invisible in a diff, and the words are what an operator actually acts on.
 *
 * Centralised here rather than exported from BookingDetails.js so neither surface
 * imports the other's page. Same pattern as `config/guestRequestLabels.js`.
 *
 * Arabic-only, per the Arabic-first mandate. If arrival ever joins the translated
 * surface, these constants become t() calls in each consumer — the import
 * boundary is what makes that a small change.
 */

/**
 * The verdict itself. Keys are the resolver's `status` values.
 *
 * 🎨 The colour choice IS the honesty rule made visible: NO_EVIDENCE stays
 * NEUTRAL, never amber or red. A warning colour would assert "the guest failed to
 * arrive" — the exact claim this feature exists not to make. Only ARRIVED earns
 * colour.
 *
 * ⚠️ NEVER «لم يدخل بعد». NO_EVIDENCE means "we looked and found nothing", not
 * "the guest did not arrive": shared building codes are the highest-traffic
 * entries on the fleet and belong to no booking, so the negative claim is
 * frequently false. This wording is a locked decision, not a phrasing preference.
 */
/**
 * `standalone` is the wording to use where NOTHING NEARBY SAYS WHAT THIS IS ABOUT.
 *
 * The booking page renders these inside a card titled «حالة تسجيل دخول الضيف», so «غير معروف» is
 * unambiguous there and a longer form would just be noise in a badge. The inbox panel has no title
 * — it is one line beside a door code — and «غير معروف» alone left an operator asking "unknown
 * WHAT?", which was visible the moment it reached staging.
 *
 * Only NOT_APPLICABLE needs it: the other two already name their own subject, and prefixing them
 * would produce «الدخول: دخل الضيف». Consumers should read `standalone || label`, so a state that
 * never needs the long form simply does not carry one.
 */
export const ARRIVAL_VIEW = {
  ARRIVED: { label: 'دخل الضيف', cls: 'arrival-badge-arrived' },
  NO_EVIDENCE: { label: 'لا يوجد دخول مسجّل', cls: 'arrival-badge-none' },
  NOT_APPLICABLE: {
    label: 'غير معروف',
    standalone: 'حالة الدخول غير معروفة',
    cls: 'arrival-badge-unknown'
  }
};

/**
 * Why we could not look. Keys are the resolver's `reason` values.
 * Kept OUT of the badge so the state stays scannable and the explanation stays
 * honest — «غير معروف» alone would invite a guess.
 */
export const ARRIVAL_REASON_AR = {
  NO_LOCK: 'لا يوجد قفل ذكي مرتبط بهذا العقار.',
  NO_BOOKING_CODE: 'لا يوجد رمز خاص بهذا الحجز — أي دخول سيكون برمز مشترك لا يمكن نسبته للضيف.',
  NO_ACCESS_WINDOW: 'لم تُحدَّد فترة صلاحية للرمز — الرمز لم يصل إلى القفل.',
  STAY_NOT_STARTED: 'لم تبدأ فترة صلاحية الرمز بعد.',
  BOOKING_CANCELLED: 'الحجز ملغي.',
  EVIDENCE_EXPIRED: 'انتهت مدة حفظ سجلات القفل لهذه الفترة (٩٠ يوماً).'
};

/**
 * The fallback when `reason` is absent or not in the map above.
 *
 * Shared for the same reason the map is: the two surfaces had DIFFERENT fallback
 * behaviour — the booking page always said something, the panel silently rendered
 * nothing — which left «غير معروف» standing alone inviting exactly the guess the
 * map exists to prevent. Centralising the vocabulary without centralising the
 * fallback just moves the drift to the render site.
 *
 * Note `REASON.NO_MATCHING_EVENT` deliberately has no entry above: it only ever
 * accompanies NO_EVIDENCE, which shows its own hint instead.
 */
export const ARRIVAL_REASON_UNKNOWN =
  'لا تتوفر بيانات كافية لتحديد حالة تسجيل دخول الضيف.';

/**
 * The arrival lookup itself failed, while the rest of the payload survived.
 * Distinct from any verdict: we did not fail to FIND evidence, we failed to LOOK.
 * Deliberately short — it shares a line with the door code.
 */
export const ARRIVAL_UNAVAILABLE_SHORT = 'حالة الدخول غير متاحة';

/**
 * Said once, plainly, wherever we come up empty: absence of a record is not
 * absence of a guest.
 */
export const ARRIVAL_NO_EVIDENCE_HINT =
  'لم نعثر على دخول برمز هذا الحجز. هذا لا يعني أن الضيف لم يصل — قد يكون دخل برمز مشترك.';

/**
 * A broken lookup says so, out loud. Silence would be the same lying-by-omission
 * the whole feature exists to prevent — and it must never be read as evidence
 * either way about the guest.
 */
export const ARRIVAL_ERROR_AR =
  'تعذّر التحقق من حالة تسجيل دخول الضيف لهذا الحجز. هذه مشكلة تقنية — لا تُقرأ كدليل على وصول الضيف أو عدمه.';
